import React, { createContext, useContext } from 'react';
import { useSelector } from 'react-redux';
// Newer versions expose <CopilotKit/> root component instead of CopilotProvider
import { CopilotKit } from '@copilotkit/react-core';
import { API_URL } from '../utils/constant';

/**
 * Copilot Context for managing chat functionality
 */
const CopilotContext = createContext({
  organisationId: '',
  authToken: '',
  apiUrl: '',
  isAuthenticated: false,
  // Legacy fields removed: chatHistory, sendMessage, isLoading
});

/**
 * Custom hook to use Copilot context
 */
export const useCopilot = () => useContext(CopilotContext);

/**
 * CopilotProvider Component (React 18)
 *
 * Wraps the official CopilotKit provider while exposing a light custom context
 * for legacy components (e.g., our floating chat) to keep using sendMessage & chatHistory.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Copilot provider component
 */
const CopilotProvider = ({ children }) => {

  // Resolve organization ID robustly from Redux and storage
  const stateOrgId = useSelector(state => (
    state?.summary?.localClient?._id ||
    state?.summary?.clients?.[0]?._id ||
    state?.auth?.user?.organisationId?._id ||
    state?.auth?.user?.organisationId ||
    state?.auth?.user?.organisation?._id ||
    null
  ));

  let storageOrgId = null;
  try {
    const orgRaw = localStorage.getItem('org');
    if (orgRaw) {
      const org = JSON.parse(orgRaw);
      storageOrgId = org?._id || org?.organisationId || org?.id || null;
    }
  } catch (_) {}

  const fallbackVendor = sessionStorage.getItem('vendorId');
  const organisationId = stateOrgId || storageOrgId || fallbackVendor || '65741905f25d82d21ced1abd';

  // Get authentication token
  const authToken = localStorage.getItem('session') || '';
  const isAuthenticated = !!authToken;

  // Construct API base (env override -> origin) and final endpoint with org id
  const apiBase = API_URL;
  
  const apiUrl = `${apiBase}/${organisationId}/chatbot`;

  // Custom fetcher to inject auth header & org id for CopilotKit internal GraphQL operations
  const backend = {
    fetcher: async (body) => {
      // Prevent API call if user is not authenticated
      if (!isAuthenticated || !authToken) {
        console.warn('Chatbot API call blocked: User is not authenticated');
        throw new Error('User not authenticated. Please log in to use the chatbot.');
      }

      try {
        const res = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify(body)
        });
        const json = await res.json();
        return json; // Unmodified response so CopilotKit handles it natively
      } catch (e) {
        console.error('Copilot backend passthrough error', e);
        throw e;
      }
    }
  };

  return (
    <CopilotContext.Provider value={{ organisationId, authToken, isAuthenticated, apiUrl }}>
      <div className="copilot-provider-root">
        {isAuthenticated ? (
          <CopilotKit
            runtimeUrl={apiUrl}
            agent="my_agent"
            backend={backend}
            options={{
              contextItems: [
                { name: 'organisationId', text: organisationId },
                { name: 'authStatus', text: 'authenticated' }
              ]
            }}
          >
            {children}
          </CopilotKit>
        ) : (
          // Render children without CopilotKit when not authenticated
          children
        )}
      </div>
    </CopilotContext.Provider>
  );
};

export default CopilotProvider;
